import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { Target, TrendingDown, Landmark, Sparkles, RefreshCw, Bot, History, ChevronDown } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions, db } from '../lib/firebase';
import { collection, query, where, orderBy, getDocs, doc, getDoc } from 'firebase/firestore';

interface DebtAdvisorProps {
  householdId: string;
}

export default function DebtAdvisor({ householdId }: DebtAdvisorProps) {
  const [strategy, setStrategy] = useState<'snowball' | 'avalanche' | 'cashflowdam'>('snowball');
  const [isGenerating, setIsGenerating] = useState(false);
  const [data, setData] = useState<any>(null);
  const [pastReports, setPastReports] = useState<any[]>([]);
  const [archiveExpanded, setArchiveExpanded] = useState(false);
  const [loadingInitial, setLoadingInitial] = useState(true);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Fetch past reports
        const reportsQuery = query(
          collection(db, 'estate_items'),
          where('householdId', '==', householdId),
          where('category', '==', 'advisor_report'),
          orderBy('createdAt', 'desc')
        );
        const reportsSnap = await getDocs(reportsQuery);
        const reportsList = reportsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setPastReports(reportsList);

        if (reportsList.length > 0) {
          // Fetch the projections to reconstruct the full data state
          const planSnap = await getDoc(doc(db, 'debt_crush_plans', householdId));
          if (planSnap.exists()) {
            const planData = planSnap.data();
            setData({
              advice: reportsList[0].details,
              projections: planData.projections,
              unlockDate: planData.unlockDate || null
            });
          }
        }
      } catch (err) {
        console.error('Error fetching initial advisor data:', err);
      } finally {
        setLoadingInitial(false);
      }
    };

    fetchInitialData();
  }, [householdId]);

  const loadPastReport = (report: any) => {
    if (data && data.projections) {
      setData({
        ...data,
        advice: report.details
      });
      setArchiveExpanded(false);
    }
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    try {
      const getStrategy = httpsCallable(functions, 'generateDebtPayoffStrategy');
      const response = await getStrategy({});
      const newData = response.data as any;
      setData(newData);
      
      // Prepend the new report to the archive list
      const newReport = {
        id: 'temp-' + Date.now(),
        title: `Debt & Wealth Strategy - ${new Date().toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`,
        details: newData.advice,
        createdAt: { toDate: () => new Date() }
      };
      setPastReports(prev => [newReport, ...prev]);
    } catch (err) {
      console.error('Failed to generate debt strategy:', err);
      alert('Failed to generate strategy. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const renderMarkdown = (text: string) => {
    return text.split('\n').map((line, i) => {
      if (line.startsWith('###')) return <h3 key={i} className="text-lg font-bold mt-6 mb-2 text-slate-800">{line.replace('###', '')}</h3>;
      if (line.startsWith('##')) return <h2 key={i} className="text-xl font-bold mt-8 mb-4 text-indigo-900 border-b pb-2">{line.replace('##', '')}</h2>;
      if (line.startsWith('#')) return <h1 key={i} className="text-2xl font-bold mt-8 mb-4 text-slate-900">{line.replace('#', '')}</h1>;
      if (line.startsWith('-') || line.startsWith('*')) return <li key={i} className="ml-4 list-disc">{line.replace(/^[-*]/, '')}</li>;
      if (line.trim() === '') return <br key={i} />;
      
      const parts = line.split(/(\*\*.*?\*\*)/g);
      return (
        <p key={i} className="mb-2">
          {parts.map((part, index) => {
            if (part.startsWith('**') && part.endsWith('**')) {
              return <strong key={index} className="font-semibold text-slate-900">{part.slice(2, -2)}</strong>;
            }
            return part;
          })}
        </p>
      );
    });
  };

  const getConsumerProjection = () => {
    if (!data) return [];
    if (strategy === 'snowball') return data.projections.snowball;
    if (strategy === 'avalanche') return data.projections.avalanche;
    return data.projections.cashflowdam;
  };

  const consumerData = getConsumerProjection();
  const mortgageData = data?.projections?.mortgage || [];

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="p-6 bg-white border-b border-slate-200 shrink-0">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Debt & Wealth Advisor</h2>
            <p className="text-slate-500 mt-1">Holistic AI analysis for debt acceleration and the Smith Maneuver.</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleGenerate}
              disabled={isGenerating}
              className="flex items-center gap-2 bg-indigo-600 text-white px-5 py-2.5 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 font-medium shadow-sm"
            >
              <RefreshCw className={`w-4 h-4 ${isGenerating ? 'animate-spin' : ''}`} />
              {isGenerating ? 'Analyzing...' : 'Generate Full Strategy'}
            </button>
          </div>
        </div>

        {data && (
          <div className="flex bg-slate-100 p-1 rounded-xl max-w-xl">
            <button
              onClick={() => setStrategy('snowball')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium rounded-lg transition-colors ${strategy === 'snowball' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              <Target className="w-4 h-4" /> Snowball
            </button>
            <button
              onClick={() => setStrategy('avalanche')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium rounded-lg transition-colors ${strategy === 'avalanche' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              <TrendingDown className="w-4 h-4" /> Avalanche
            </button>
            <button
              onClick={() => setStrategy('cashflowdam')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium rounded-lg transition-colors ${strategy === 'cashflowdam' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              <Landmark className="w-4 h-4" /> Cash Flow Dam
            </button>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-6 bg-slate-50">
        {!data ? (
          <div className="h-full flex flex-col items-center justify-center text-slate-400">
            <Sparkles className="w-12 h-12 mb-4 opacity-20" />
            <p className="font-semibold text-slate-600 mb-2">Ready to Destroy Your Debt?</p>
            <p className="text-sm mt-2 text-slate-500 max-w-md text-center">
              Click Generate Strategy to have the AI crunch your entire financial profile and build a step-by-step mathematical trajectory for Snowball, Avalanche, Cash Flow Dam, and the Smith Maneuver.
            </p>
          </div>
        ) : (
          <div className="space-y-8 max-w-6xl mx-auto">

            {/* Past Reports Archive (Cabinet) */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div
                onClick={() => setArchiveExpanded(prev => !prev)}
                className="px-4 sm:px-6 py-4 flex justify-between items-center cursor-pointer hover:bg-slate-50 transition-colors bg-slate-50/50"
              >
                <div className="flex items-center gap-3">
                  <History className="w-5 h-5 text-indigo-600" />
                  <h3 className="font-bold text-slate-800">Past Reports Archive</h3>
                  <span className="text-xs font-semibold text-slate-400">({pastReports.length})</span>
                </div>
                <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${archiveExpanded ? 'rotate-180' : ''}`} />
              </div>

              {archiveExpanded && (
                <div className="p-4 sm:p-6 border-t border-slate-100 space-y-3 bg-white">
                  {pastReports.length === 0 ? (
                    <div className="text-center text-slate-500 py-6 text-sm">
                      No past reports found.
                    </div>
                  ) : (
                    pastReports.map((report) => (
                      <button
                        key={report.id}
                        onClick={() => loadPastReport(report)}
                        className="w-full text-left p-4 rounded-xl border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors group"
                      >
                        <div className="flex justify-between items-center mb-2">
                          <h4 className="font-bold text-slate-900 group-hover:text-indigo-900">{report.title}</h4>
                          <span className="text-xs text-slate-400">
                            {report.createdAt?.toDate ? report.createdAt.toDate().toLocaleDateString() : new Date(report.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-sm text-slate-500 line-clamp-2">
                          {report.details.replace(/[#*]/g, '')}
                        </p>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Consumer Debt Graph */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
              <h3 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2">
                Consumer Debt Payoff Projection: {strategy === 'snowball' ? 'Snowball' : strategy === 'avalanche' ? 'Avalanche' : 'Cash Flow Dam'}
              </h3>
              <div className="h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={consumerData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="month" stroke="#64748b" tick={{fontSize: 12}} />
                    <YAxis stroke="#64748b" tick={{fontSize: 12}} tickFormatter={(value) => `$${value}`} />
                    <Tooltip 
                      formatter={(value: number) => [`$${value.toLocaleString()}`, '']}
                      contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="totalDebt" name="Total Consumer Debt" stroke="#4f46e5" strokeWidth={3} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Mortgage Graph */}
            {mortgageData.length > 0 && (
              <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                <h3 className="text-lg font-bold text-slate-900 mb-2 flex items-center gap-2">
                  [LOCKED IN] Smith Maneuver: Debt-to-Equity Tracking
                </h3>
                <p className="text-sm text-slate-500 mb-6">Tracking your STEP Mortgage trajectory towards the 85% HELOC activation threshold.</p>
                
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={mortgageData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="month" stroke="#64748b" tick={{fontSize: 12}} />
                      <YAxis yAxisId="left" stroke="#64748b" tick={{fontSize: 12}} tickFormatter={(value) => `$${value}`} />
                      <YAxis yAxisId="right" orientation="right" stroke="#0ea5e9" tick={{fontSize: 12}} tickFormatter={(value) => `${value}%`} />
                      <Tooltip 
                        contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                      />
                      <Legend />
                      <ReferenceLine yAxisId="right" y={85} label="85% HELOC Unlock" stroke="#ef4444" strokeDasharray="3 3" />
                      <Line yAxisId="left" type="monotone" dataKey="mortgageBalance" name="Mortgage Balance" stroke="#0f172a" strokeWidth={2} dot={false} />
                      <Line yAxisId="right" type="monotone" dataKey="dte" name="Debt-to-Equity %" stroke="#0ea5e9" strokeWidth={3} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Investment Graph */}
            {data?.projections?.investments && data.projections.investments.length > 0 && (
              <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                <h3 className="text-lg font-bold text-slate-900 mb-2 flex items-center gap-2">
                  Investment Portfolio Compounder (30 Years)
                </h3>
                <p className="text-sm text-slate-500 mb-6">Projected growth of your existing investment balances based on expected annualized returns.</p>
                
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data.projections.investments} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="month" stroke="#64748b" tick={{fontSize: 12}} />
                      <YAxis stroke="#64748b" tick={{fontSize: 12}} tickFormatter={(value) => `$${value}`} />
                      <Tooltip 
                        formatter={(value: number) => [`$${value.toLocaleString()}`, 'Portfolio Value']}
                        contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                      />
                      <Legend />
                      <Line type="monotone" dataKey="portfolioValue" name="Total Portfolio Value" stroke="#10b981" strokeWidth={3} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* AI Advice Feed */}
            <div className="bg-indigo-50 p-8 rounded-xl border border-indigo-100">
              <div className="flex items-center gap-3 mb-6 pb-6 border-b border-indigo-200">
                <div className="w-10 h-10 bg-indigo-600 text-white rounded-lg flex items-center justify-center">
                  <Bot className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-indigo-950">AI Strategist Report</h3>
              </div>
              <div className="prose prose-slate max-w-none prose-headings:text-indigo-950 text-slate-700">
                {renderMarkdown(data.advice)}
              </div>
            </div>

          </div>
        )}
      </div>
    </div>
  );
}
